/**
 * Queries y mutations de contenido (sections, subsections, articles, search).
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import type {
  Article,
  RecentArticle,
  SearchResult,
  Section,
  SectionTreeNode,
  Subsection,
} from '@/lib/types';

export const contentKeys = {
  tree: ['content', 'tree'] as const,
  section: (slug: string) => ['content', 'section', slug] as const,
  subsection: (s: string, ss: string) => ['content', 'subsection', s, ss] as const,
  article: (s: string, ss: string, a: string) => ['content', 'article', s, ss, a] as const,
  recent: ['content', 'recent'] as const,
  featured: ['content', 'featured'] as const,
};

const invalidateAll = (qc: ReturnType<typeof useQueryClient>) =>
  qc.invalidateQueries({ queryKey: ['content'] });

// ----------------------- Reads -----------------------

export function useNavTree() {
  return useQuery({
    queryKey: contentKeys.tree,
    queryFn: () => api<{ sections: SectionTreeNode[] }>('/api/sections/tree'),
    staleTime: 30_000,
  });
}

export function useSection(slug: string) {
  return useQuery({
    queryKey: contentKeys.section(slug),
    queryFn: () =>
      api<{ section: Section & { subsections: Subsection[] } }>(`/api/sections/${slug}`),
  });
}

export function useSubsection(sectionSlug: string, slug: string) {
  return useQuery({
    queryKey: contentKeys.subsection(sectionSlug, slug),
    queryFn: () =>
      api<{
        section: Section;
        subsection: Subsection & { articles: Array<{ id: string; slug: string; titulo: string }> };
      }>(`/api/subsections/${sectionSlug}/${slug}`),
  });
}

export function useArticle(sectionSlug: string, subsectionSlug: string, slug: string) {
  return useQuery({
    queryKey: contentKeys.article(sectionSlug, subsectionSlug, slug),
    queryFn: () =>
      api<{
        section: Section;
        subsection: Subsection;
        article: Article;
      }>(`/api/articles/${sectionSlug}/${subsectionSlug}/${slug}`),
  });
}

export function useRecentArticles(limit = 8) {
  return useQuery({
    queryKey: [...contentKeys.recent, limit],
    queryFn: () => api<{ articles: RecentArticle[] }>(`/api/articles/recent?limit=${limit}`),
  });
}

export function useFeaturedArticles() {
  return useQuery({
    queryKey: contentKeys.featured,
    queryFn: () => api<{ articles: RecentArticle[] }>('/api/articles/featured'),
  });
}

export function useSearch(query: string, enabled = true) {
  return useQuery({
    queryKey: ['search', query],
    queryFn: () => api<{ results: SearchResult[] }>(`/api/search?q=${encodeURIComponent(query)}`),
    enabled: enabled && query.trim().length > 1,
    staleTime: 10_000,
  });
}

// ----------------------- Mutations: artículos -----------------------

export interface UpdateArticleData {
  titulo?: string;
  resumen?: string;
  contentMd?: string;
  destacado?: boolean;
  publicado?: boolean;
  orden?: number;
}

export function useUpdateArticle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateArticleData }) =>
      api<{ article: Article }>(`/api/articles/${id}`, {
        method: 'PATCH',
        body: data,
      }),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useCreateArticle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      subsectionId: string;
      titulo: string;
      contentMd?: string;
      resumen?: string;
    }) => api<{ article: Article }>('/api/articles', { method: 'POST', body: data }),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useDeleteArticle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      api<{ ok: true }>(`/api/articles/${id}`, { method: 'DELETE' }),
    onSuccess: () => invalidateAll(qc),
  });
}

// ----------------------- Mutations: subsecciones -----------------------

export interface UpdateSubsectionData {
  titulo?: string;
  descripcion?: string;
  contentMd?: string;
  orden?: number;
}

export function useUpdateSubsection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateSubsectionData }) =>
      api<{ subsection: Subsection }>(`/api/subsections/${id}`, {
        method: 'PATCH',
        body: data,
      }),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useCreateSubsection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      sectionId: string;
      titulo: string;
      descripcion?: string;
      contentMd?: string;
    }) => api<{ subsection: Subsection }>('/api/subsections', { method: 'POST', body: data }),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useDeleteSubsection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      api<{ ok: true }>(`/api/subsections/${id}`, { method: 'DELETE' }),
    onSuccess: () => invalidateAll(qc),
  });
}

// ----------------------- Mutations: secciones -----------------------

export interface UpdateSectionData {
  titulo?: string;
  descripcion?: string;
  icono?: string;
  orden?: number;
}

export function useUpdateSection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateSectionData }) =>
      api<{ section: Section }>(`/api/sections/${id}`, {
        method: 'PATCH',
        body: data,
      }),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useCreateSection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { titulo: string; descripcion?: string; icono?: string }) =>
      api<{ section: Section }>('/api/sections', { method: 'POST', body: data }),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useDeleteSection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      api<{ ok: true }>(`/api/sections/${id}`, { method: 'DELETE' }),
    onSuccess: () => invalidateAll(qc),
  });
}
